// application.js
import request from 'superagent';
import { setSchedules, setReturnSchedules } from '../actions/ScheduleActions.js';
import config from '../config';

class ScheduleService {
  get(query, isReturn) {
    return new Promise((resolve, reject) => {
      request.post(config.apiUrl+'schedule/search')
      .send(query)
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer '+localStorage.getItem('token'))
      .end((error, response) => {
        if (!error && response.statusCode >= 200 && response.statusCode <= 304) {
          if(isReturn) {
            resolve(setReturnSchedules(response.body));  
          }
          else {
            resolve(setSchedules(response.body));
          }
        }
        else if(error){
          reject('Error: '+JSON.stringify(error));
        }
        else{
          reject(response.body);
        }
      });
    })
  }

  /**
   * Get seat availability and pricing info for a specific trip
   * @param {string} scheduleBus - Schedule bus ID
   * @param {string} slot - Date (YYYY-MM-DD format)
   * @param {string} [sessionId] - Optional session ID to exclude own holds from temporaryTakenSeats
   * @returns {Promise<Object>} { takenSeats, temporaryTakenSeats, pricing, busLayout, busType }
   */
  getSeatInfo(scheduleBus, slot, sessionId, departCity, arriveCity) {
    let url = `${config.apiUrl}schedule/seats/${scheduleBus}/${slot}`;
    const params = [];
    if (sessionId) {
      params.push(`session_id=${encodeURIComponent(sessionId)}`);
    }
    if (departCity) {
      params.push(`depart_city=${encodeURIComponent(departCity)}`);
    }
    if (arriveCity) {
      params.push(`arrive_city=${encodeURIComponent(arriveCity)}`);
    }
    if (params.length) {
      url += '?' + params.join('&');
    }
    return new Promise((resolve, reject) => {
      request.get(url)
        .set('Accept', 'application/json')
        .end((error, response) => {
          if (!error && response.ok) {
            resolve(response.body);
          } else if (error) {
            reject(error);
          } else {
            reject(response.body);
          }
        });
    });
  }

  /**
   * Hold a seat temporarily for a user session
   * @param {string} scheduleBus - Schedule bus ID
   * @param {string} slot - Date (YYYY-MM-DD format)
   * @param {string} seatNumber - Seat identifier (e.g. "1A")
   * @param {string} sessionId - Frontend-generated session UUID
   * @returns {Promise<Object>} { success, hold_id, expires_at }
   */
  holdSeat(scheduleBus, slot, seatNumber, sessionId) {
    return new Promise((resolve, reject) => {
      request.post(`${config.apiUrl}schedule/seats/hold`)
        .send({ schedule_bus: scheduleBus, slot, seat_number: seatNumber, session_id: sessionId })
        .set('Accept', 'application/json')
        .end((error, response) => {
          if (!error && response.ok) {
            resolve(response.body);
          } else if (response && response.status === 409) {
            reject({ conflict: true, message: (response.body && response.body.error) || 'Seat is no longer available', temporaryTakenSeats: (response.body && response.body.temporaryTakenSeats) || null, takenSeats: (response.body && response.body.takenSeats) || null });
          } else if (error) {
            reject(error);
          } else {
            reject(response.body);
          }
        });
    });
  }

  /**
   * Release a single held seat
   * @param {string} scheduleBus - Schedule bus ID
   * @param {string} slot - Date (YYYY-MM-DD format)
   * @param {string} seatNumber - Seat identifier (e.g. "1A")
   * @param {string} sessionId - Frontend-generated session UUID
   * @returns {Promise<Object>} { success }
   */
  releaseSeat(scheduleBus, slot, seatNumber, sessionId) {
    return new Promise((resolve, reject) => {
      request.del(`${config.apiUrl}schedule/seats/hold`)
        .send({ schedule_bus: scheduleBus, slot, seat_number: seatNumber, session_id: sessionId })
        .set('Accept', 'application/json')
        .end((error, response) => {
          if (!error && response.ok) {
            resolve(response.body);
          } else if (error) {
            reject(error);
          } else {
            reject(response.body);
          }
        });
    });
  }

  /**
   * Release all held seats for a session (bulk release)
   * @param {string} sessionId - Frontend-generated session UUID
   * @returns {Promise<Object>} { success, released }
   */
  releaseAllHolds(sessionId) {
    return new Promise((resolve, reject) => {
      request.del(`${config.apiUrl}schedule/seats/hold/session`)
        .send({ session_id: sessionId })
        .set('Accept', 'application/json')
        .end((error, response) => {
          if (!error && response.ok) {
            resolve(response.body);
          } else if (error) {
            reject(error);
          } else {
            reject(response.body);
          }
        });
    });
  }
}

export default new ScheduleService();